import { coerceNumberProperty, NumberInput } from '@angular/cdk/coercion';
import { ChangeDetectionStrategy, Component, HostBinding, Input, OnDestroy } from '@angular/core';
import { BehaviorSubject, combineLatest, distinctUntilChanged, map, switchMap, take, tap } from 'rxjs';

import {
  AgentSkillFacade,
  AgentUserPlanningService,
  ChanEnum,
  Channel,
  ChannelUtil,
  distinctUntilSerializeChanged,
  isNotNullOrUndefined,
  logObserver,
  PlanningFacade,
  UntilDestroy,
  untilDestroyed,
} from '@contact/upgrade';

@UntilDestroy()
@Component({
  selector: '[cng-agent-planning-row-skill]',
  templateUrl: 'agent-planning-row-skill.component.html',
  styleUrls: ['./agent-planning-row-skill.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentPlanningRowSkillComponent implements OnDestroy {
  @Input()
  set channel(value: Channel) {
    if (!!value) {
      this._channel$.next(value);
    }
  }
  get channel$() {
    return this._channel$.pipe(distinctUntilSerializeChanged());
  }
  private readonly _channel$ = new BehaviorSubject<Channel | undefined>(undefined);

  @Input()
  set skillId(value: string) {
    if (!!value) {
      this._skillId$.next(value);
    }
  }
  get skillId$() {
    return this._skillId$.pipe(distinctUntilChanged());
  }
  private readonly _skillId$ = new BehaviorSubject<string | undefined>(undefined);

  @Input()
  set index(value: NumberInput) {
    if (!!value) {
      this._index$.next(coerceNumberProperty(value));
    }
  }
  get index$() {
    return this._index$.pipe(distinctUntilChanged());
  }
  private readonly _index$ = new BehaviorSubject<number>(-1);

  /**
   * Class open
   */
  @HostBinding('class.inactive')
  get shouldClassInactive() {
    return !this.active;
  }

  readonly media$ = combineLatest([this.channel$.pipe(isNotNullOrUndefined()), this.skillId$.pipe(isNotNullOrUndefined())]);
  readonly activable$ = this.media$.pipe(switchMap(([channel, skillId]) => this.agentSkillFacade.getActivable(skillId, channel)));
  readonly skill$ = this.activable$.pipe(
    map(activable => activable && activable.entity && activable.entity.skill),
    distinctUntilSerializeChanged()
  );
  readonly active$ = this.activable$.pipe(
    map(activable => !!activable && activable.active),
    distinctUntilChanged()
  );
  readonly canActive$ = this.activable$.pipe(
    map(activable => !!activable && activable.canActive),
    distinctUntilChanged()
  );
  readonly dirty$ = this.activable$.pipe(
    map(activable => !!activable && activable.dirty),
    distinctUntilChanged()
  );
  readonly channelStr$ = this.channel$.pipe(map(channel => ChannelUtil.toString(channel || ChanEnum.NotSet)));
  readonly slots$ = this.media$.pipe(
    switchMap(([channel, skillId]) => this.planningService.getSlots(skillId, channel)),
    tap(logObserver('slots')),
    distinctUntilSerializeChanged()
  );
  readonly planning$ = this.media$.pipe(switchMap(([channel, skillId]) => this.planningFacade.getById(skillId, channel)));
  readonly underInstruction$ = this.planning$.pipe(map(planning => !!planning && planning.underInstruction));

  private active = false;

  constructor(
    private readonly planningService: AgentUserPlanningService,
    private readonly planningFacade: PlanningFacade,
    private readonly agentSkillFacade: AgentSkillFacade
  ) {}

  ngOnInit() {
    this.active$.pipe(untilDestroyed(this)).subscribe(active => (this.active = active));
  }

  ngOnDestroy() {
    this._channel$.complete();
    this._skillId$.complete();
    this._index$.complete();
  }

  toogleSkillActivation(newValue: boolean): void {
    this.media$
      .pipe(take(1), untilDestroyed(this))
      .subscribe(([channel, skillId]) => this.agentSkillFacade.changeActivation(skillId, channel, newValue));
  }
}
